import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import { ROUTES, VOVER_LANDING_URL } from '@/constants';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { apiService } from '@services/api';
import logger from '@utils/logger';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';

type AccountDeletionStatus =
  | 'pending_email_code'
  | 'email_code_confirmed'
  | 'scheduled'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

type AccountDeletionDoc = {
  _id: string;
  tenantId: string;
  status: AccountDeletionStatus;
  requestedAt?: string;
  confirmedAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  error?: { message?: string } | null;
};

function normalizeCode(raw: string) {
  return raw.replace(/\D/g, '').slice(0, 6);
}

// Fluxo protegido (apenas autenticado + criador da organização)
const AccountDeletionPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [notOwner, setNotOwner] = useState(false);
  const [doc, setDoc] = useState<AccountDeletionDoc | null>(null);
  const [deletePhrase, setDeletePhrase] = useState('');
  const [requested, setRequested] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const redirectTimer = useRef<number | null>(null);

  const canRequest = deletePhrase.trim().toUpperCase() === 'DELETE';
  const isProcessing = Boolean(doc?.status && ['scheduled', 'processing'].includes(doc.status));

  const statusLabel = useMemo(() => {
    switch (doc?.status) {
      case 'pending_email_code':
        return 'Aguardando código por e-mail';
      case 'scheduled':
      case 'processing':
        return 'Processando exclusão';
      case 'completed':
        return 'Concluído';
      case 'failed':
        return 'Falhou';
      default:
        return doc?.status || '—';
    }
  }, [doc?.status]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await apiService.getAccountDeletionStatus();
      if (data?.success) {
        setIsAuthenticated(true);
        setNotOwner(false);
        setDoc(data.data);
      } else {
        setIsAuthenticated(true);
      }
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setIsAuthenticated(false);
      } else if (status === 403) {
        setIsAuthenticated(true);
        setNotOwner(true);
      } else {
        logger.error('Account deletion status load failed', e);
        setError('Não foi possível carregar o status de exclusão.');
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      await loadStatus();
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
      if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
    };
  }, [loadStatus]);

  // Poll enquanto estiver processando
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!doc?.status) return;
    if (!['scheduled', 'processing'].includes(doc.status)) return;

    const id = window.setInterval(() => {
      loadStatus().catch(() => null);
    }, 5000);
    return () => window.clearInterval(id);
  }, [doc?.status, isAuthenticated, loadStatus]);

  // Redirect pós-conclusão
  useEffect(() => {
    if (doc?.status !== 'completed') return;
    redirectTimer.current = window.setTimeout(() => {
      window.location.replace(VOVER_LANDING_URL);
    }, 5000);
  }, [doc?.status]);

  const handleRequest = async () => {
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await apiService.requestAccountDeletion();
      if (data?.success) {
        setRequested(true);
        setMessage(data?.message || 'Código enviado por e-mail.');
        await loadStatus();
      } else {
        setError('Não foi possível solicitar a exclusão.');
      }
    } catch (e: unknown) {
      setError(getFriendlyErrorMessage(e, 'Não foi possível solicitar a exclusão.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await apiService.confirmAccountDeletionCode(code);
      if (data?.success) {
        setMessage('Exclusão confirmada. Iniciando processamento...');
        await loadStatus();
      } else {
        setError('Código inválido ou expirado.');
      }
    } catch (e: unknown) {
      setError(getFriendlyErrorMessage(e, 'Código inválido ou expirado.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="text" onClick={() => navigate(ROUTES.HOME)}>
            Voltar para a página inicial
          </Button>
          <Button variant="text" component={RouterLink} to={ROUTES.LOGIN}>
            Acessar o app
          </Button>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Exclusão de conta
        </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fluxo de exclusão no aplicativo (apenas para o criador da conta).
          </Typography>

        <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Importante
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            A exclusão é um processo <strong>irreversível</strong> e remove os dados operacionais da organização associada à conta. Somente o{' '}
            <strong>criador da conta</strong> (e-mail de contato do cadastro) pode solicitar a exclusão.
          </Typography>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Passo a passo
          </Typography>
          <Box component="ol" sx={{ pl: 3, lineHeight: 1.9, mb: 2 }}>
            <li>Faça login no Vover com o e-mail do criador da conta.</li>
            <li>Abra esta página e siga o fluxo de exclusão.</li>
            <li>Solicite o código e confirme com o código de 6 dígitos enviado por e-mail.</li>
            <li>Após confirmação, a exclusão é iniciada automaticamente.</li>
          </Box>

          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            <strong>SLA:</strong> até <strong>7 dias corridos</strong> após a confirmação válida do código. O acesso ao sistema é bloqueado imediatamente após a
            confirmação.
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            <strong>Retenção mínima:</strong> billing por até <strong>5 anos</strong> e logs de auditoria por até <strong>90 dias</strong>.
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            <strong>Pagamentos pendentes:</strong> mesmo com pagamentos pendentes ou acesso bloqueado por inadimplência, o criador pode solicitar a exclusão.
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Dúvidas: <MuiLink href="mailto:contato@vover.com.br">contato@vover.com.br</MuiLink>
          </Typography>
        </Box>

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !isAuthenticated ? (
            <Alert severity="info">
              Para excluir a conta, faça login com o e-mail do criador.
            </Alert>
          ) : notOwner ? (
            <Alert severity="warning">Apenas o criador da conta pode solicitar a exclusão.</Alert>
          ) : (
            <Box sx={{ mt: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Fluxo de exclusão (criador)
              </Typography>

              {message && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {doc?.status && (
                <Alert severity={doc.status === 'completed' ? 'success' : doc.status === 'failed' ? 'error' : 'info'} sx={{ mb: 2 }}>
                  <strong>Status:</strong> {statusLabel}
                  {doc.status === 'failed' && doc.error?.message ? ` — ${doc.error.message}` : null}
                </Alert>
              )}

              {doc?.status === 'completed' ? (
                <Alert severity="success">
                  Conta excluída com sucesso. Você será redirecionado para a landing em 5 segundos.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Para prosseguir, digite <strong>DELETE</strong> e solicite o código por e-mail.
                  </Typography>

                  <TextField
                    label='Digite "DELETE" para confirmar'
                    value={deletePhrase}
                    onChange={(e) => setDeletePhrase(e.target.value)}
                    fullWidth
                    disabled={submitting || isProcessing}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    disabled={!canRequest || submitting || isProcessing}
                    onClick={handleRequest}
                    sx={{ mb: 2 }}
                  >
                    Enviar código por e-mail
                  </Button>

                  {(requested || doc?.status === 'pending_email_code') && (
                    <>
                      <TextField
                        label="Código de 6 dígitos"
                        value={code}
                        onChange={(e) => setCode(normalizeCode(e.target.value))}
                        inputProps={{ inputMode: 'numeric' }}
                        fullWidth
                        disabled={submitting || isProcessing}
                        sx={{ mb: 2 }}
                      />

                      <Button
                        variant="contained"
                        fullWidth
                        disabled={code.length !== 6 || submitting || isProcessing}
                        onClick={handleConfirm}
                      >
                        Confirmar exclusão
                      </Button>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AccountDeletionPage;

